import React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FixtureActionButton, FixturesShowcase, StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolMatchCreateValues,
  buildSchoolMatchFields,
  buildSchoolMatchUpdateValues,
} from "pages/app/workspace/sections/forms/school-ops-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";
import {
  SchoolMatchesSectionContentStack,
  SchoolMatchesSectionPanelCard,
  SchoolMatchesSectionSectionHeader,
  SchoolMatchesSectionSectionBody,
  SchoolMatchesSectionEyebrowText,
  SchoolMatchesSectionBodyText,
} from "pages/app/workspace/sections/styles/school-matches-section.inline.styles";

export const SchoolMatchesSection: React.FC<{
  embedded?: boolean;
  listBackTo?: string;
  portalAccess: any;
}> = ({ embedded = false, listBackTo = "/workspace/schedule?view=list", portalAccess }) => {
  const history = useHistory();
  /*
    TODO(school-ops-ux): This section previously supported an "All scoped schools" view
    (empty school filter) so admins could browse fixtures across every school in scope.
    The global School Ops header selector now always binds a single activeSchoolId.
    Revisit during the broader School Ops UX refactor — e.g. an "All schools" header
    option, a cross-school fixtures view, or a dedicated multi-school desk.
  */
  const { schoolId, schools } = useScopedSchoolSelection({
    portalAccess,
  });
  const matchesQuery = useQuery({
    queryKey: ["workspace-school-matches", schoolId],
    queryFn: () =>
      workspaceApi.schoolMatches.list({
        school_id: schoolId || undefined,
      }),
  });
  const regionsQuery = useQuery({
    queryKey: ["workspace-school-match-regions"],
    queryFn: () => workspaceApi.administrativeRegions.list(),
  });
  const teamDirectoryQuery = useQuery({
    queryKey: [
      "workspace-school-match-team-directory",
      schools.map((school: any) => school.id).join("|"),
    ],
    queryFn: async () => {
      const results = await Promise.all(
        schools.map((school: any) => workspaceApi.schoolTeams.list(school.id)),
      );
      return results.flat();
    },
    enabled: schools.length > 0,
  });
  const canManageSchoolMatches = hasPortalAction(portalAccess, "school_matches.manage");
  const teamDirectory = teamDirectoryQuery.data || [];
  const matchFields = buildSchoolMatchFields({
    regions: regionsQuery.data || [],
    schoolId,
    teamDirectory,
  });

  const renderFixtureActions = (fixture: any) => (
    <>
      {canManageSchoolMatches ? (
        <FixtureActionButton
          $variant="link"
          onClick={() =>
            history.push({
              pathname: `/workspace/school-scoresheets/${fixture.id}`,
              state: {
                backTo: listBackTo,
              },
            })
          }
          type="button"
        >
          Open scoresheet
        </FixtureActionButton>
      ) : null}
      <FixtureActionButton
        $variant="primary"
        onClick={() =>
          history.push({
            pathname: `/workspace/school-matches/${fixture.id}`,
            state: {
              backTo: listBackTo,
            },
          })
        }
        type="button"
      >
        Open match
      </FixtureActionButton>
    </>
  );

  return (
    <>
      <CrudSection
        buildCreateValues={() =>
          buildSchoolMatchCreateValues({
            schoolId,
            teamDirectory,
          })
        }
        buildUpdateValues={buildSchoolMatchUpdateValues}
        canCreate={canManageSchoolMatches}
        canEdit={canManageSchoolMatches}
        columns={[
          {
            label: "Fixture",
            accessor: (row) =>
              `${row.home_school_team?.name || "Home"} vs ${row.away_school_team?.name || "Away"}`,
          },
          {
            label: "Competition",
            accessor: (row) => row.competition_name || "TBC",
          },
          {
            label: "Status",
            accessor: (row) => <StatusPill value={row.status} />,
          },
          {
            label: "Sport",
            accessor: (row) => row.sport_name || "Unknown",
          },
          {
            label: "Scheduled",
            accessor: (row) => formatDateTime(row.scheduled_at),
          },
        ]}
        createLabel="Add school match"
        data={matchesQuery.data || []}
        description={
          canManageSchoolMatches
            ? "Create and update school fixtures with scoped team, venue, live-link, countdown, status, and result controls."
            : "Review school fixtures, live coverage links, and countdown states currently visible to this role."
        }
        fields={matchFields}
        invalidateKeys={[["workspace-school-matches", schoolId]]}
        isLoading={matchesQuery.isLoading}
        onCreate={(payload) => workspaceApi.schoolMatches.create(payload)}
        onUpdate={(row, payload) => workspaceApi.schoolMatches.update(row.id, payload)}
        renderList={({ data, emptyDescription, emptyTitle, isLoading, onEdit, toolbar }) => (
          <SchoolMatchesSectionContentStack>
            {toolbar ? (
              <SchoolMatchesSectionPanelCard>
              <SchoolMatchesSectionSectionHeader>
                <SchoolMatchesSectionEyebrowText>Context</SchoolMatchesSectionEyebrowText>
                <SchoolMatchesSectionBodyText>
                  Focus the fixtures below by school before editing live links, countdowns, or
                  results.
                </SchoolMatchesSectionBodyText>
              </SchoolMatchesSectionSectionHeader>
              <SchoolMatchesSectionSectionBody>{toolbar}</SchoolMatchesSectionSectionBody>
              </SchoolMatchesSectionPanelCard>
            ) : null}
            <FixturesShowcase
              editLabel="Edit fixture"
              emptyDescription={emptyDescription}
              emptyTitle={emptyTitle}
              fixtures={data}
              isLoading={isLoading}
              onEditFixture={onEdit}
              renderFixtureActions={renderFixtureActions}
            />
          </SchoolMatchesSectionContentStack>
        )}
        title={embedded ? "Fixtures" : "School Matches"}
        embedded={embedded}
        showTitle={!embedded}
      />
    </>
  );
};
